import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import {
  fetchUserById,
  updateUser,
  fetchModulesPermissions,
  fetchUserPermissions,
  updateUserPermissions,
  clearUserPermissions
} from '../../../../store/slices/adminSlice';
import { utilityService } from '../../../../services/utilityService';
import SearchableSelect from '../../../../components/SearchableSelect/SearchableSelect';
import toast from 'react-hot-toast';

const UserEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    selectedUser: user,
    loading,
    error,
    modulesPermissions,
    userPermissions,
    myPermissions,
    permissionsLoading,
    updatingPermissions
  } = useAppSelector((state) => state.admin);
  const { user: loggedInUser } = useAppSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    account_type: '',
    user_type: '',
    email: '',
    country_id: '',
    mobile_number: '',
    status: 'Active'
  });

  const [selectedPerms, setSelectedPerms] = useState([]);
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);

  // Derive isSubAdmin (the user being edited)
  const isSubAdmin = useMemo(() => {
    const type = String(formData.user_type || '').toLowerCase();
    return type === 'subadmin' || type === 'sub_admin';
  }, [formData.user_type]);

  // True when the logged-in user is a SubAdmin (not a super-admin)
  const isSubAdminUser = loggedInUser?.user_type === 'SubAdmin';

  // Set of permission IDs the logged-in SubAdmin owns — restricts what they can assign
  const myPermIdSet = useMemo(() => {
    if (!isSubAdminUser || !myPermissions) return null; // null = no restriction (Admin)
    return new Set(myPermissions.permission_ids.map(Number));
  }, [isSubAdminUser, myPermissions]);

  /**
   * Returns true if this permission checkbox is locked (read-only).
   * Locked when logged-in user is a SubAdmin and does NOT own that permission.
   */
  const isPermLocked = (permId) => {
    if (!myPermIdSet) return false;
    return !myPermIdSet.has(Number(permId));
  };

  // Initial fetch and cleanup
  useEffect(() => {
    if (id) {
      dispatch(fetchUserById(id));
      dispatch(fetchModulesPermissions());
    }
    return () => {
      dispatch(clearUserPermissions());
    };
  }, [id, dispatch]);

  // Fetch countries
  useEffect(() => {
    const loadCountries = async () => {
      try {
        setCountriesLoading(true);
        const response = await utilityService.getCountries();
        if (response.data) setCountries(response.data);
      } catch (err) {
        console.error('Failed to fetch countries:', err);
      } finally {
        setCountriesLoading(false);
      }
    };
    loadCountries();
  }, []);

  // Fetch user permissions if SubAdmin
  useEffect(() => {
    if (id && isSubAdmin) {
      dispatch(fetchUserPermissions(id));
    }
  }, [id, isSubAdmin, dispatch]);

  // Sync selectedPerms when userPermissions loads from API
  useEffect(() => {
    if (userPermissions) {
      const apiPermIds = userPermissions.permission_ids || userPermissions.data?.permission_ids;
      if (Array.isArray(apiPermIds)) {
        setSelectedPerms(apiPermIds.map(pid => Number(pid)));
      }
    }
  }, [userPermissions]);

  // Populate form when user data is available
  useEffect(() => {
    if (user && String(user.id) === String(id)) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        account_type: user.account_type || '',
        user_type: user.user_type || '',
        email: user.email || '',
        country_id: user.country_id || user.country?.id || '',
        mobile_number: user.mobile_number || '',
        status: user.status || 'Active'
      });
      if (user.profileImage) setImagePreview(user.profileImage);
    }
  }, [user, id]);

  // Force default selections if state stays empty after load (resolves visual-only selection issues)
  useEffect(() => {
    if (!loading && user) {
      setFormData(prev => ({
        ...prev,
        account_type: prev.account_type || 'Personal',
        country_id: prev.country_id || (countries.length > 0 ? countries[0].id : prev.country_id)
      }));
    }
  }, [loading, user, countries]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // For SubAdmins, account_type is not required
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'Please enter First Name';
    if (!formData.last_name.trim()) newErrors.last_name = 'Please enter Last Name';
    if (!formData.email.trim()) newErrors.email = 'Please enter a valid email';
    if (!isSubAdmin && !formData.account_type) newErrors.account_type = 'Please select Account Type';
    if (!formData.country_id) newErrors.country_id = 'Please select Country Code';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const updateData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        ...(isSubAdmin ? {} : { account_type: formData.account_type }),
        user_type: formData.user_type,
        email: formData.email,
        country_id: formData.country_id ? parseInt(formData.country_id, 10) : null,
        mobile_number: formData.mobile_number,
        status: formData.status
      };

      await dispatch(updateUser({ id, data: updateData })).unwrap();

      if (isSubAdmin) {
        await dispatch(updateUserPermissions({ id, permissions: selectedPerms })).unwrap();
        toast.success('Successfully saved changes');
      } else {
        toast.success('Successfully saved profile');
      }
      navigate('/admin/users');
    } catch (err) {
      toast.error(err || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const togglePermission = (permId) => {
    if (isPermLocked(permId)) return;
    const idNum = Number(permId);
    setSelectedPerms(prev =>
      prev.includes(idNum) ? prev.filter(pid => pid !== idNum) : [...prev, idNum]
    );
  };

  const toggleCheckAll = (module) => {
    const ownedIds = module.permissions
      .map(p => Number(p.id))
      .filter(id => !isPermLocked(id));
    if (ownedIds.length === 0) return;
    const allChecked = ownedIds.every(pid => selectedPerms.includes(pid));
    if (allChecked) {
      setSelectedPerms(prev => prev.filter(pid => !ownedIds.includes(pid)));
    } else {
      setSelectedPerms(prev => [...new Set([...prev, ...ownedIds])]);
    }
  };

  // Convert string to Title Case
  const toTitleCase = (str) => {
    if (!str) return '';
    
    const specialCases = {
      'dataexport': 'Data Export',
      'data export': 'Data Export',
      'data_export': 'Data Export',
      'checkall': 'Check All',
      'check all': 'Check All',
      'check_all': 'Check All',
      'jetsetter': 'JetSetter',
      'jet_setter': 'Jet Setter',
      'jet setter': 'Jet Setter',
      'airliner': 'Airliner',
      'airliners': 'Airliner',
      'sub_admin': 'Sub Admin',
      'subadmin': 'SubAdmin',
      'venders': 'Vendor',
      'vender': 'Vendor',
      'cms': 'CMS',
      'notifications': 'Messages',
      'notification': 'Messages',
      'contract_management': 'Contract Management',
      'contractmanagement': 'Contract Management',
      'account_settings': 'Account Settings',
      'accountsettings': 'Account Settings',
      'data_analysis': 'Data Analysis',
      'dataanalysis': 'Data Analysis',
      'user_management': 'User Management',
      'usermanagement': 'User Management'
    };
    
    const normalized = str.toLowerCase().trim().replace(/-/g, '_');
    if (specialCases[normalized]) {
      return specialCases[normalized];
    }
    
    return str
      .toLowerCase()
      .trim()
      .split(/[\s_-]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Color mapping for permission types
  const getPermissionColor = (permName) => {
    const colorMap = {
      'index': 'bg-red-500',
      'export': 'bg-green-500',
      'add': 'bg-cyan-500',
      'edit': 'bg-blue-500',
      'view': 'bg-orange-500',
      'archive': 'bg-yellow-400',
      'delete': 'bg-yellow-300'
    };
    return colorMap[permName?.toLowerCase()] || 'bg-gray-400';
  };

  const getPermissionLabel = (permName) => {
    if (permName.toLowerCase() === 'export') return 'Data Export';
    return toTitleCase(permName);
  };

  // Modules that should only show "Check All" (feature permission modules)
  const featurePermissionModuleIds = [12]; // Refund Approval only

  const permissionHeaders = useMemo(() => {
    const headers = new Set();
    modulesPermissions.forEach(mod => {
      // Skip individual permissions for feature permission modules
      if (featurePermissionModuleIds.includes(mod.id)) {
        return;
      }
      mod.permissions.forEach(p => {
        const permName = p.name.toLowerCase().replace(/\s+/g, '').replace(/_/g, '').replace(/-/g, '');
        if (permName !== 'checkall') {
          headers.add(p.name);
        }
      });
    });
    return Array.from(headers);
  }, [modulesPermissions]);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-normal text-gray-800">Edit {formData.user_type || 'User'}</h1>
        <button 
          onClick={() => navigate(-1)} 
          className="bg-[#ffd200] px-6 py-2 rounded text-black font-semibold flex items-center gap-2 hover:opacity-90 transition-opacity"
        >
          <i className="fas fa-arrow-left"></i>
          <span>Back</span>
        </button>
      </div>

      <div className="bg-white rounded shadow-sm p-6">
        <form onSubmit={handleSubmit}>
          {/* Row 1: First Name | Last Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mb-6">
            <div>
              <label className="block text-sm font-normal mb-2 text-gray-700">
                First Name <span className="text-red-500">*</span>
                <span className="relative inline-block ml-1 group">
                  <i className="fas fa-info-circle text-black-500 text-xs cursor-help"></i>
                  <span className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-48 bg-gray-800 text-white text-xs rounded py-2 px-3 z-10">
                    Enter your First Name
                    <span className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-gray-800"></span>
                  </span>
                </span>
              </label>
              <input 
                name="first_name" 
                value={formData.first_name} 
                onChange={handleInputChange} 
                className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 uppercase ${errors.first_name ? 'border-red-500' : 'border-gray-300'}`} 
              />
              {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
            </div>

            <div>
              <label className="block text-sm font-normal mb-2 text-gray-700">
                Last Name <span className="text-red-500">*</span>
                <span className="relative inline-block ml-1 group">
                  <i className="fas fa-info-circle text-black-500 text-xs cursor-help"></i>
                  <span className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-48 bg-gray-800 text-white text-xs rounded py-2 px-3 z-10">
                    Enter your Last Name
                    <span className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-gray-800"></span>
                  </span>
                </span>
              </label>
              <input 
                name="last_name" 
                value={formData.last_name} 
                onChange={handleInputChange} 
                className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 uppercase ${errors.last_name ? 'border-red-500' : 'border-gray-300'}`} 
              />
              {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
            </div>
          </div>

          {/* Row 2: Account Type | E-mail */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mb-6">
            {!isSubAdmin && (
              <div>
                <label className="block text-sm font-normal mb-2 text-gray-700">
                  Account Type <span className="text-red-500">*</span>
                  <i className="fas fa-info-circle text-black-500 text-xs cursor-help"></i>
                </label>
                <SearchableSelect
                  name="account_type"
                  value={formData.account_type}
                  onChange={handleInputChange}
                  options={[
                    { value: 'Personal', label: 'Personal' },
                    { value: 'Business', label: 'Business' }
                  ]}
                  placeholder="Select Account Type"
                  className={errors.account_type ? 'border-red-500' : ''}
                />
              </div>
            )}

            <div className={isSubAdmin ? 'md:col-span-2' : ''}>
              <label className="block text-sm font-normal mb-2 text-gray-700">
                Email <span className="text-red-500">*</span>
                <i className="fas fa-info-circle text-black-500 text-xs cursor-help"></i>
              </label>
              <input
                name="email"
                value={formData.email}
                readOnly
                className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded text-sm text-gray-500 cursor-not-allowed uppercase"
              />
            </div>
          </div>

          {/* Row 3: Country Code | Mobile Number | Status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-6 mb-6">
            <div>
              <label className="block text-sm font-normal mb-2 text-gray-700">
                Country Code <span className="text-red-500">*</span>
                <i className="fas fa-info-circle text-black-500 text-xs cursor-help"></i>
              </label>
              <SearchableSelect
                name="country_id"
                value={formData.country_id}
                onChange={handleInputChange}
                options={countries.map(c => ({
                  value: c.id,
                  label: `${c.phone_code} (${c.name})`
                }))}
                placeholder="Select Country Code"
                className={errors.country_id ? 'border-red-500' : ''}
              />
            </div>

            <div>
              <label className="block text-sm font-normal mb-2 text-gray-700">
                Mobile Number <span className="text-red-500">*</span>
                <i className="fas fa-info-circle text-black-500 text-xs cursor-help"></i>
              </label>
              <input 
                name="mobile_number" 
                value={formData.mobile_number} 
                onChange={handleInputChange} 
                placeholder="Mobile Number"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" 
              />
            </div>

            <div>
              <label className="block text-sm font-normal mb-2 text-gray-700">
                Status <span className="text-red-500">*</span>
                <i className="fas fa-info-circle text-black-500 text-xs cursor-help"></i>
              </label>
              <SearchableSelect
                name="status"
                value={formData.status === 'Inactive' ? 'Deactive' : formData.status}
                onChange={(e) => handleInputChange({
                  target: { name: 'status', value: e.target.value }
                })}
                options={[
                  { value: 'Active', label: 'Active' },
                  { value: 'Deactive', label: 'Inactive' }
                ]}
                placeholder="Select Status"
              />
            </div>
          </div>

          {/* Permissions Section */}
          {isSubAdmin && (
            <div className="mt-10">
              <h2 className="text-base font-semibold mb-4 text-gray-800">Set Permissions</h2>
              {permissionsLoading ? (
                <div className="p-10 text-center text-gray-500 flex flex-col items-center gap-2">
                  <i className="fas fa-circle-notch fa-spin text-2xl text-yellow-500"></i>
                  <span>Loading permission modules...</span>
                </div>
              ) : (
                <div className="overflow-x-auto bg-white">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-white">
                        <th className="p-3 text-left font-normal text-gray-700 text-sm"></th>
                        {permissionHeaders.map((header) => (
                          <th key={header} className="p-3 text-left font-normal text-gray-700 text-xs">
                            <div className="flex items-center gap-2">
                              <span className={`w-3 h-3 rounded-sm ${getPermissionColor(header)}`}></span>
                            </div>
                          </th>
                        ))}
                        <th className="p-3 text-left font-normal text-gray-700 text-xs"></th>
                      </tr>
                    </thead>

                    <tbody>
                      {modulesPermissions.map((module) => {
                        const isFeaturePermModule = featurePermissionModuleIds.includes(module.id);
                        
                        return (
                          <tr key={module.id} className="hover:bg-gray-50 transition-colors">
                            <td className="p-3 text-gray-700 text-sm font-semibold" style={{ textTransform: 'none' }}>{toTitleCase(module.name)}</td>

                            {isFeaturePermModule ? (
                              // For feature permission modules, render empty cells for each header column
                              permissionHeaders.map((header) => (
                                <td key={header} className="p-3"></td>
                              ))
                            ) : (
                              // For regular modules, render individual permission checkboxes
                              permissionHeaders.map((header) => {
                                const perm = module.permissions.find(p => p.name === header);
                                const locked = perm ? isPermLocked(perm.id) : false;
                                return (
                                  <td key={header} className="p-3">
                                    {perm ? (
                                      <label className={`flex items-center gap-2 ${locked ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}>
                                        <input
                                          type="checkbox"
                                          checked={selectedPerms.includes(Number(perm.id))}
                                          onChange={() => togglePermission(perm.id)}
                                          disabled={locked}
                                          className="w-4 h-4 rounded border-gray-300"
                                        />
                                        <span className="text-xs text-gray-600 font-semibold whitespace-nowrap" style={{ textTransform: 'none' }}>{getPermissionLabel(header)}</span>
                                      </label>
                                    ) : null}
                                  </td>
                                );
                              })
                            )}

                            <td className="p-3">
                              {(() => {
                                const ownedInModule = module.permissions.filter(p => !isPermLocked(p.id));
                                const allOwned = ownedInModule.length > 0 && ownedInModule.every(p => selectedPerms.includes(Number(p.id)));
                                const hasOwned = ownedInModule.length > 0;
                                return (
                                  <label className={`flex items-center gap-2 ${hasOwned ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}`}>
                                    <input
                                      type="checkbox"
                                      checked={allOwned}
                                      onChange={() => toggleCheckAll(module)}
                                      disabled={!hasOwned}
                                      className="w-4 h-4 rounded border-gray-300"
                                    />
                                    <span className="text-xs text-gray-600 font-semibold whitespace-nowrap" style={{ textTransform: 'none' }}>Check All</span>
                                  </label>
                                );
                              })()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-6 mt-6">
            <button 
              type="submit" 
              disabled={submitting || updatingPermissions} 
              className="bg-[#ffd200] px-8 py-2 rounded font-semibold text-black disabled:opacity-50 hover:opacity-90 transition-opacity"
            >
              {submitting ? 'Saving...' : 'Save'}
            </button>
            <button 
              type="button" 
              onClick={() => navigate('/admin/users')} 
              className="bg-white border border-gray-300 px-8 py-2 rounded font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserEditor;
